import { apiClient } from '@/auth';

export type ClinicianResponse = {
  id: number;
  hospital_id?: number;
  hospital_ids: number[];
  first_name: string;
  last_name: string;
  email: string;
  email_verified?: boolean;
  phone_number?: string;
  bio?: string;
  is_meal_admin?: boolean;
  is_report_admin?: boolean;
  is_report_previewer?: boolean;
  is_internal?: boolean;
  direct_patient_email_permitted?: boolean;
  locale?: string | null;
  created_time?: string;
  updated_time?: string;
  flags?: Record<string, unknown>;
};

/**
 * Body shape for `PATCH /hospitals/{hospital_id}/clinicians/{clinician_id}`
 * (MNT swagger:15409, `PatchClinicianRequest`). All fields are optional —
 * unspecified fields keep their existing value (PATCH semantics, per the
 * endpoint's swagger description).
 */
export type PatchClinicianBody = {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  bio?: string | null;
  password?: string | null;
  locale?: string | null;
};

/**
 * Body for `POST /hospitals/{hospital_id}/clinicians` (MNT swagger:15394
 * `CreateClinicianRequest`). All four name/email/bio fields are required
 * on the server; `bio` defaults to an empty string client-side when the
 * Add-Clinician form omits it. `clinic_ids` opts the new clinician into
 * specific clinics within the hospital at create time.
 */
export type CreateClinicianBody = {
  first_name: string;
  last_name: string;
  email: string;
  bio: string;
  phone_number?: string;
  password?: string | null;
  preferred_contact?: boolean | null;
  is_notified_of_report?: boolean | null;
  is_clinic_admin?: boolean | null;
  clinic_ids?: number[] | null;
};

export const cliniciansApi = {
  async current() {
    const res = await apiClient.get<ClinicianResponse>(`/clinicians/current`);
    return res.data;
  },

  /**
   * Edit clinician fields. Per MNT swagger:6762, only fields included in the
   * body are written — others stay as-is. Used today for the language
   * switcher to persist `locale` to the clinician profile so the choice
   * follows the user across devices.
   */
  async patch(hospitalId: number, clinicianId: number, body: PatchClinicianBody) {
    const res = await apiClient.patch<ClinicianResponse>(
      `/hospitals/${hospitalId}/clinicians/${clinicianId}`,
      body,
    );
    return res.data;
  },

  /**
   * Create a new clinician in the hospital. POST /hospitals/{id}/clinicians
   * (MNT swagger:6723). Server emails the new clinician a password-reset
   * link by default — V2 doesn't need to follow up.
   */
  async create(hospitalId: number, body: CreateClinicianBody) {
    const res = await apiClient.post<ClinicianResponse>(
      `/hospitals/${hospitalId}/clinicians`,
      body,
    );
    return res.data;
  },

  /**
   * All clinicians registered in the hospital. GET /hospitals/{id}/clinicians
   * (MNT swagger:6706). Powers the Clinicians page list.
   */
  async listByHospital(hospitalId: number): Promise<ClinicianResponse[]> {
    const res = await apiClient.get<ClinicianResponse[]>(
      `/hospitals/${hospitalId}/clinicians`,
    );
    return Array.isArray(res.data) ? res.data : [];
  },
};
